import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { CheckCircle, ReceiptLong } from '@mui/icons-material';
import { toAmericanOdds } from './OddsToggle';
import { BookMarket, bookProb } from '../utils/bookMode';
import { MAX_SLIP_BETS } from '../utils/slipStore';
import { SegmentedToggle } from './SegmentedToggle';
import { parseAmericanOdds } from '../utils/parlay';
import {
  CONFIDENCE_LABEL,
  EdgeConfidence,
  evaluateBet,
  hitsLabel,
  MAX_MARGINAL_STAKE,
  MAX_STAKE,
  verdict,
} from '../utils/betValue';

// Per-viewer conveniences kept in this browser only (the panel works without them).
const BANKROLL_KEY = 'ltb.bankroll';
const KELLY_KEY = 'ltb.kellyFraction';
const readStored = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};
const writeStored = (key: string, value: string) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* storage unavailable: keep it in memory only */
  }
};

const CONFIDENCE_COLOR: Record<EdgeConfidence, 'success' | 'warning' | 'default' | 'error'> = {
  clear: 'success',
  marginal: 'warning',
  within: 'default',
  negative: 'error',
};

const pct = (p: number, digits = 1) => `${(p * 100).toFixed(digits)}%`;
const money = (v: number) => `${v >= 0 ? '+' : '−'}$${Math.abs(v).toFixed(2)}`;

const Row: React.FC<{ label: React.ReactNode; value: React.ReactNode; strong?: boolean }> = ({ label, value, strong }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
    <Typography variant="body2" color={strong ? 'text.primary' : 'text.secondary'} sx={{ fontWeight: strong ? 600 : 400 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: strong ? 600 : 400, fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
      {value}
    </Typography>
  </Stack>
);

interface BetValuePanelProps {
  /** Our probability that the bet wins. */
  prob: number;
  /** Our uncertainty at this probability, in percentage points (from the backtests). */
  bandPts: number;
  /** Optional heading, e.g. "Kyle Larson top 10". */
  title?: string;
  /** Autofocus the odds field (popover use). */
  autoFocus?: boolean;
  /** When set, also show the price a book would typically post for this market (book mode). */
  bookMarket?: BookMarket;
  /** The typical book price when it isn't one market's book-mode price (a parlay's); takes precedence over bookMarket. */
  typicalBook?: number;
  /** Extra breakdown shown under the verdict once the book's odds are in, given the book's implied probability. */
  renderGap?: (implied: number) => React.ReactNode;
  /** When set, a priced bet can be added to the Slip Builder (odds as typed, stake from the suggestion if a bankroll is
   * set). Returns false if the bet can't go on a slip. */
  onAddToSlip?: (oddsText: string, stakeText: string) => boolean;
  /** Bets already on the Slip Builder, for the note next to the button. */
  slipCount?: number;
  /** Why this bet can't go on a slip (the button shows, disabled, with this reason). */
  addDisabledReason?: string;
}

/**
 * "Is this bet worth it?": the book's price against our probability. Edge, EV, whether the edge is bigger than our
 * uncertainty at this probability, and a fractional-Kelly stake. Decision support, not advice.
 */
export const BetValuePanel: React.FC<BetValuePanelProps> = ({
  prob,
  bandPts,
  title,
  autoFocus,
  bookMarket,
  typicalBook: typicalBookProp,
  renderGap,
  onAddToSlip,
  slipCount,
  addDisabledReason: disabledReasonProp,
}) => {
  const [added, setAdded] = useState<string | null>(null);
  // A full slip can't take another bet (unless this one is already on it).
  const addDisabledReason =
    disabledReasonProp ?? ((slipCount ?? 0) >= MAX_SLIP_BETS ? `Your slip is full (${MAX_SLIP_BETS} bets).` : undefined);
  const [bookOdds, setBookOdds] = useState('');
  const [bankroll, setBankroll] = useState(() => readStored(BANKROLL_KEY) ?? '');
  const [kellyFraction, setKellyFraction] = useState(() => Number(readStored(KELLY_KEY)) || 0.25);
  useEffect(() => writeStored(BANKROLL_KEY, bankroll), [bankroll]);
  useEffect(() => writeStored(KELLY_KEY, String(kellyFraction)), [kellyFraction]);

  const odds = parseAmericanOdds(bookOdds);
  // The odds that go on the slip: what you typed if it's a valid price, otherwise left blank to fill in there.
  const addOdds = odds !== null ? bookOdds : '';
  const value = odds !== null && prob > 0 && prob < 1 ? evaluateBet(prob, odds, bandPts, kellyFraction) : null;
  const bank = Number(bankroll.replace(/[$,\s]/g, ''));
  const typicalBook = typicalBookProp ?? (bookMarket ? bookProb(prob, bookMarket) : null);
  // Book prices usually sit within about ±0.5 on the log-odds scale of the book-mode price (the fit's typical error),
  // so a price well beyond that means this book is out of line with how books normally price this market.
  const logOdds = (p: number) => Math.log(p / (1 - p));
  const unusuallyLong =
    typicalBook !== null && value !== null && value.implied > 0 && value.implied < 1 && typicalBook < 1 &&
    logOdds(typicalBook) - logOdds(value.implied) > 0.5;
  const hasBank = Number.isFinite(bank) && bank > 0;

  return (
    <Box>
      {title && (
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          {title}
        </Typography>
      )}
      <Row
        label="Our probability"
        value={
          <>
            {pct(prob)}{' '}
            <Tooltip title="Our uncertainty at this probability level: how far the production model's predictions at this level were from what happened in backtests, combined with how much equally good models disagree.">
              <Typography component="span" variant="caption" color="text.secondary">
                ±{bandPts.toFixed(1)}
              </Typography>
            </Tooltip>{' '}
            ({prob > 0 && prob < 1 ? toAmericanOdds(prob) : '–'})
          </>
        }
      />
      {prob > 0 && prob < 1 && <Row label="Hits about" value={hitsLabel(prob)} />}
      {typicalBook !== null && typicalBook > 0 && typicalBook < 1 && (
        <Row
          label={
            <Tooltip title="What a sportsbook would typically post for this, given our probability: our odds with the field flattened and a normal book margin added (book mode).">
              <span>Typical book price</span>
            </Tooltip>
          }
          value={`${toAmericanOdds(typicalBook)} (${pct(typicalBook)})`}
        />
      )}
      <TextField
        size="small"
        label="Book's odds"
        placeholder="+450"
        value={bookOdds}
        onChange={(e) => setBookOdds(e.target.value)}
        error={bookOdds.trim() !== '' && odds === null}
        helperText={bookOdds.trim() !== '' && odds === null ? 'American odds, e.g. +450 or -150' : ' '}
        autoFocus={autoFocus}
        fullWidth
        sx={{ mt: 1.5 }}
      />
      {value && (
        <Stack spacing={0.4}>
          <Row label="Book implies" value={pct(value.implied)} />
          <Row
            label="Edge"
            value={`${value.edgePts >= 0 ? '+' : ''}${value.edgePts.toFixed(1)} pts (${value.edgeRel >= 0 ? '+' : ''}${(value.edgeRel * 100).toFixed(0)}%)`}
          />
          <Row label="Expected value" value={`${money(value.ev100)} per $100`} strong />
          <Box sx={{ pt: 0.75 }}>
            <Chip size="small" color={CONFIDENCE_COLOR[value.confidence]} label={CONFIDENCE_LABEL[value.confidence]} />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ pt: 0.5, lineHeight: 1.5 }}>
            {verdict(value)}
          </Typography>
          {renderGap?.(value.implied)}
          {/* The gap breakdown covers an out-of-line price itself. */}
          {unusuallyLong && !renderGap && (
            <Typography variant="body2" sx={{ pt: 0.5, lineHeight: 1.5, color: 'primary.main' }}>
              This price is much longer than books usually post here ({toAmericanOdds(typicalBook!)}): this book is out
              of line on this one.
            </Typography>
          )}
          {(value.confidence === 'clear' || value.confidence === 'marginal') && (
            <Box sx={{ pt: 1 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Suggested stake
                </Typography>
                <SegmentedToggle
                  aria-label="Kelly fraction"
                  size="small"
                  value={kellyFraction === 0.5 ? 'half' : 'quarter'}
                  onChange={(v) => setKellyFraction(v === 'half' ? 0.5 : 0.25)}
                  options={[
                    { value: 'quarter', label: '¼ Kelly' },
                    { value: 'half', label: '½ Kelly' },
                  ]}
                />
              </Stack>
              <Row
                label={
                  <Tooltip
                    title={`Kelly sizes a bet by edge ÷ odds. Fractional Kelly allows for our probabilities not being exact. Clear edges are capped at ${MAX_STAKE * 100}% of bankroll; marginal edges get half the stake, capped at ${MAX_MARGINAL_STAKE * 100}%. Full Kelly here: ${pct(value.fullKelly)}.`}
                  >
                    <span>Of bankroll</span>
                  </Tooltip>
                }
                value={
                  <>
                    <strong>{pct(value.stake, 2)}</strong>
                    {hasBank && ` = $${(bank * value.stake).toFixed(2)}`}
                  </>
                }
              />
              <TextField
                size="small"
                label="Bankroll (optional)"
                value={bankroll}
                onChange={(e) => setBankroll(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                sx={{ mt: 1 }}
                fullWidth
              />
            </Box>
          )}
        </Stack>
      )}
      {/* Always offered once priced: without the book's odds the bet goes on the slip with the odds left to fill in. */}
      {(onAddToSlip || addDisabledReason) && (
        <Box sx={{ mt: 1.5 }}>
          <Button
            fullWidth
            disabled={!!addDisabledReason}
            // Once added (at these odds), a green outline with a check instead of a grayed-out button.
            variant={added === addOdds ? 'outlined' : 'contained'}
            startIcon={added === addOdds ? <CheckCircle /> : <ReceiptLong />}
            aria-disabled={added === addOdds}
            onClick={() => {
              if (added === addOdds || !onAddToSlip) return;
              const stake = value && hasBank && value.stake > 0 ? String(Math.round(value.stake * bank)) : '';
              if (onAddToSlip(addOdds, stake)) setAdded(addOdds);
            }}
          >
            {added === addOdds ? 'Added to Slip Builder' : 'Add to Slip Builder'}
          </Button>
          {(addDisabledReason || odds === null) && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
              {addDisabledReason ?? "Add the book's odds here or on the slip."}
            </Typography>
          )}
          {!!slipCount && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
              {slipCount} bet{slipCount === 1 ? '' : 's'} on the slip
            </Typography>
          )}
        </Box>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25 }}>
        Decision support from model estimates, not betting advice.
      </Typography>
    </Box>
  );
};
